'use client';

import { CollectionBannerElementDataType } from '@/components/sections/builder/elements/body/collection-banner/type';
import { ElementProps } from '@/components/sections/builder/types';
import { cn } from '@/lib/utils';
import { useSite } from '@/stores/sites';
import { HttpTypes } from '@/types';
import Image from 'next/image';
import { useParams } from 'next/navigation';

const CoverBanner = ({
  data,
  themeData,
}: {
  data: HttpTypes.StoreCollection;
  themeData: CollectionBannerElementDataType;
}) => {
  const { slug } = useParams<{ slug: string }>();

  return (
    <>
      <section
        className='relative overflow-hidden bg-gradient-to-r from-pink-100 to-purple-100'
        style={{
          backgroundImage: `url(${data?.img_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className='absolute inset-0 bg-black bg-opacity-20'></div>
        <div className='container relative mx-auto flex h-full items-center px-10'>
          <div className='collection_title_color max-w-md'>
            <h1 className='mb-4 text-4xl font-bold md:text-5xl'>
              {slug === 'all' ? 'All Product' : data?.collection_name}
            </h1>
            {themeData.isShowDescription && (
              <p
                className='text-lg leading-relaxed md:text-xl'
                dangerouslySetInnerHTML={{ __html: data?.description || '' }}
              />
            )}
          </div>
        </div>
      </section>
      <style jsx>{`
        @media (min-width: 320px) {
          section {
            height: 180px;
            min-height: 250px;
            background-position: left;
          }
        }

        @media (min-width: 480px) {
          section {
            height: 300px;
            min-height: 250px;
            background-position: left;
          }
        }

        @media (min-width: 720px) {
          section {
            height: ${themeData.bannerHeight}px;
          }
        }
        .collection_title_color {
          color: ${themeData.colorScheme?.text} !important;
        }
      `}</style>
    </>
  );
};

const BoxBanner = ({
  data,
  themeData,
}: {
  data: HttpTypes.StoreCollection;
  themeData: CollectionBannerElementDataType;
}) => {
  const { slug } = useParams<{ slug: string }>();

  return (
    <>
      <section className='theme_layout_page_width mx-auto py-10 duration-100'>
        <div className='container mx-auto max-w-screen-xl px-4'>
          <div className='flex h-full flex-col items-stretch justify-between gap-16 md:flex-row'>
            {slug === 'all' ? (
              <h1 className='theme_font_heading text-4xl font-semibold'>All Product</h1>
            ) : (
              <>
                <div className='flex w-full flex-col space-y-3 md:w-1/2'>
                  <h1 className='theme_font_heading text-4xl'>
                    {slug === 'all' ? 'All Product' : data?.collection_name}
                  </h1>
                  {themeData.isShowDescription && (
                    <div
                      className='relative'
                      dangerouslySetInnerHTML={{
                        __html: data?.description || '<p>Description Fallback</p>',
                      }}
                    />
                  )}
                </div>
                {themeData.isShowImage && data?.img_url && (
                  <div className='flex w-full items-stretch md:w-1/2'>
                    <div className='relative h-full min-h-[200px] w-full'>
                      <Image
                        src={data?.img_url as string}
                        alt='collection banner'
                        fill
                        objectFit='cover'
                        className='rounded-2xl'
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default function ElementContentCollectionBanner({
  element,
  data,
  preview,
}: ElementProps<HttpTypes.StoreCollection>) {
  const content = element.data as CollectionBannerElementDataType;
  let collectionData = data;
  const siteData = useSite((state) => state.data);

  if (preview && siteData) {
    collectionData = siteData as HttpTypes.StoreCollection;
  }

  return (
    <>
      <section className={cn('bg_collection_banner bg-white')}>
        {(content.bannerStyle === 'box' || !content.bannerStyle) && (
          <BoxBanner
            data={collectionData || ({} as HttpTypes.StoreCollection)}
            themeData={content}
          />
        )}
        {content.bannerStyle === 'cover' && (
          <CoverBanner
            data={collectionData || ({} as HttpTypes.StoreCollection)}
            themeData={content}
          />
        )}
      </section>
      <style jsx>
        {`
          .bg_collection_banner {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
        `}
      </style>
    </>
  );
}
